import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";

const SRC = { chosen: "your choice", item: "from item", auto: "automatic" };

export function PostingPreview({ draft, onChangeLine, onChangePos, onRefreshPdf }) {
  const [p, setP] = useState(null);
  const key = JSON.stringify({ c: draft.customer_id, pos: draft.place_of_supply_override, e: draft.export_type,
    l: (draft.lines || []).map((l) => [l.item_id, l.income_account_id, l.hsn_code, l.is_service]) });
  useEffect(() => {
    if (!draft.customer_id) { return; }
    api.post("/sales/invoices/posting-preview", { ...draft, place_of_supply: draft.place_of_supply_override || "" })
      .then((r) => setP(r.data)).catch(() => setP(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  if (!p) return null;
  return (
    <div className="border rounded-sm p-3 bg-muted/30 space-y-3" data-testid="posting-preview">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="font-medium">How this invoice will post</span>
        <span className="px-2 py-0.5 rounded-sm bg-white border font-mono text-xs" data-testid="posting-gst-type">{p.gst_type}</span>
        <span className="text-xs text-muted-foreground">Your branch: {p.branch_state || "—"}</span>
        <div className="flex items-center gap-2">
          <span className="text-xs">Place of supply</span>
          <Select value={p.place_of_supply || ""} disabled={p.pos_locked} onValueChange={(v) => onChangePos(v === p.customer_state ? "" : v)}>
            <SelectTrigger className="h-8 w-48 text-xs" data-testid="posting-pos"><SelectValue placeholder="State" /></SelectTrigger>
            <SelectContent className="max-h-72">{p.states.map((s) => <SelectItem key={s} value={s}>{s}{s === p.customer_state ? " (customer)" : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {onRefreshPdf && <Button size="sm" variant="outline" className="h-8" onClick={onRefreshPdf} data-testid="posting-refresh-pdf">Refresh PDF</Button>}
      </div>
      <table className="w-full dense text-xs">
        <thead><tr><th className="text-left">Line</th><th className="text-left">Revenue account</th><th className="text-left">Picked</th></tr></thead>
        <tbody>{p.lines.map((l) => (
          <tr key={l.idx} data-testid={`posting-line-${l.idx}`}>
            <td>{l.idx + 1}. {l.description || "—"}</td>
            <td>
              <div className="flex items-center gap-1">
              <Select value={l.account_id || ""} onValueChange={(v) => onChangeLine(l.idx, v)}>
                <SelectTrigger className="h-8 text-xs w-96" title={l.account_name || ""} data-testid={`posting-account-${l.idx}`}><SelectValue placeholder="Pick account" /></SelectTrigger>
                <SelectContent className="max-h-72">{p.accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}</SelectContent>
              </Select>
              {l.source === "chosen" && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => onChangeLine(l.idx, "")} data-testid={`posting-auto-${l.idx}`}>Automatic</Button>}
              </div>
            </td>
            <td className="text-muted-foreground">
              <span className="flex flex-wrap gap-2">{SRC[l.source]}{l.corrected && <span className="text-amber-700">item's export/domestic ledger replaced</span>}
              {l.warning && <span className="text-red-700 inline-flex items-center gap-1" data-testid={`posting-warning-${l.idx}`}><AlertTriangle className="w-3 h-3" />{l.warning}</span>}</span>
            </td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}
